/**
 * Tab Context Menu
 *
 * Right-click context menu for tab actions.
 */

import { useTabsContext } from '@/context/TabsContext';
import type { TabItem } from '@/context/TabsContext/types';
import {
  CloseOutlined,
  ColumnHeightOutlined,
  ColumnWidthOutlined,
  CopyOutlined,
  ExportOutlined,
  PushpinFilled,
  PushpinOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Dropdown, type MenuProps, message } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { TabsSettingsModal } from './TabsSettingsModal';

type TabContextMenuProps = {
  tab: TabItem;
  children: React.ReactNode;
};

export const TabContextMenu: React.FC<TabContextMenuProps> = ({ tab, children }) => {
  const {
    pinTab,
    unpinTab,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    closeAllTabs,
    refreshTab,
    duplicateTab,
    toggleSplit,
    tabs,
  } = useTabsContext();

  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleCopyUrl = useCallback(() => {
    const url = `${window.location.origin}${tab.path}`;
    navigator.clipboard.writeText(url).then(
      () => message.success('URL copied to clipboard'),
      () => message.error('Failed to copy URL')
    );
  }, [tab.path]);

  const handleOpenInBrowserTab = useCallback(() => {
    window.open(tab.path, '_blank');
  }, [tab.path]);

  const hasTabsToRight = useMemo(() => {
    const tabIndex = tabs.findIndex((t) => t.key === tab.key);
    return tabs.slice(tabIndex + 1).some((t) => !t.pinned);
  }, [tabs, tab.key]);

  const hasOtherClosableTabs = useMemo(() => {
    return tabs.some((t) => t.key !== tab.key && !t.pinned);
  }, [tabs, tab.key]);

  const items: MenuProps['items'] = useMemo(() => {
    const menuItems: MenuProps['items'] = [];

    // Pin/Unpin
    if (tab.pinned) {
      menuItems.push({
        key: 'unpin',
        icon: <PushpinOutlined />,
        label: 'Unpin Tab',
        onClick: () => unpinTab(tab.key),
      });
    } else {
      menuItems.push({
        key: 'pin',
        icon: <PushpinFilled />,
        label: 'Pin Tab',
        onClick: () => pinTab(tab.key),
      });
    }

    menuItems.push({ type: 'divider' });

    // Refresh
    menuItems.push({
      key: 'refresh',
      icon: <ReloadOutlined />,
      label: 'Refresh',
      onClick: () => refreshTab(tab.key),
    });

    // Copy URL
    menuItems.push({
      key: 'copy-url',
      icon: <CopyOutlined />,
      label: 'Copy URL',
      onClick: handleCopyUrl,
    });

    // Open in new browser tab
    menuItems.push({
      key: 'open-browser-tab',
      icon: <ExportOutlined />,
      label: 'Open in New Browser Tab',
      onClick: handleOpenInBrowserTab,
    });

    // Duplicate tab
    menuItems.push({
      key: 'duplicate-tab',
      icon: <CopyOutlined />,
      label: 'Duplicate Tab',
      onClick: () => duplicateTab(tab.key),
    });

    menuItems.push({
      key: 'split-right',
      icon: <ColumnWidthOutlined />,
      label: 'Split Right',
      disabled: !tab,
      onClick: () => toggleSplit(tab.key, 'horizontal'),
      'data-testid': 'tab-split-right',
    } as NonNullable<MenuProps['items']>[number]);

    menuItems.push({
      key: 'split-down',
      icon: <ColumnHeightOutlined />,
      label: 'Split Down',
      disabled: !tab,
      onClick: () => toggleSplit(tab.key, 'vertical'),
      'data-testid': 'tab-split-down',
    } as NonNullable<MenuProps['items']>[number]);

    menuItems.push({ type: 'divider' });

    // Close actions
    if (!tab.pinned) {
      menuItems.push({
        key: 'close',
        icon: <CloseOutlined />,
        label: 'Close',
        onClick: () => closeTab(tab.key),
      });
    }

    menuItems.push({
      key: 'close-others',
      icon: <CloseOutlined />,
      label: 'Close Others',
      disabled: !hasOtherClosableTabs,
      onClick: () => closeOtherTabs(tab.key),
    });

    menuItems.push({
      key: 'close-right',
      icon: <CloseOutlined />,
      label: 'Close Tabs to the Right',
      disabled: !hasTabsToRight,
      onClick: () => closeTabsToRight(tab.key),
    });

    menuItems.push({
      key: 'close-all',
      icon: <CloseOutlined />,
      label: 'Close All',
      disabled: !hasOtherClosableTabs && tab.pinned,
      onClick: closeAllTabs,
    });

    menuItems.push({ type: 'divider' });

    menuItems.push({
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Tab Settings...',
      onClick: () => setSettingsOpen(true),
    });

    return menuItems;
  }, [
    tab,
    pinTab,
    unpinTab,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    closeAllTabs,
    refreshTab,
    duplicateTab,
    toggleSplit,
    handleCopyUrl,
    handleOpenInBrowserTab,
    hasTabsToRight,
    hasOtherClosableTabs,
  ]);

  return (
    <>
      <Dropdown menu={{ items }} trigger={['contextMenu']}>
        {children}
      </Dropdown>
      <TabsSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

export default TabContextMenu;
